import { useState } from "react";
import axios from "axios";
import { AFF_API, getAffHeaders } from "../../context/AffAuthContext";
import { User, Envelope, Phone, Key, Lock, Link as LinkIcon, Check, TelegramLogo, Trash } from "@phosphor-icons/react";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

export default function ProfilePage({ data, onUpdate }) {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [changing, setChanging] = useState(false);
  const [chatId, setChatId] = useState("");
  const [linking, setLinking] = useState(false);
  const linkedChatId = data.telegram_chat_id || null;

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (newPw !== confirmPw) { toast.error("Passwords don't match"); return; }
    setChanging(true);
    try {
      await axios.post(`${AFF_API}/affiliate/change-password`, {
        current_password: currentPw, new_password: newPw
      }, { headers: getAffHeaders() });
      toast.success("Password changed successfully!");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Password change failed");
    } finally { setChanging(false); }
  };

  const handleLinkTelegram = async (e) => {
    e.preventDefault();
    if (!chatId.trim() || !/^-?\d+$/.test(chatId.trim())) {
      toast.error("Enter a valid numeric Telegram chat ID");
      return;
    }
    setLinking(true);
    try {
      const res = await axios.post(
        `${AFF_API}/affiliate/telegram/link`,
        { chat_id: chatId.trim() },
        { headers: getAffHeaders() }
      );
      if (res.data.confirmation_sent) {
        toast.success("Telegram linked! Check your Telegram for a confirmation message.");
      } else {
        toast.success("Telegram linked. You'll receive direct updates on MT5 and other events.");
      }
      setChatId("");
      onUpdate && onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to link Telegram");
    } finally { setLinking(false); }
  };

  const handleUnlinkTelegram = async () => {
    if (!window.confirm("Unlink your Telegram account? You will stop receiving direct updates.")) return;
    try {
      await axios.post(`${AFF_API}/affiliate/telegram/unlink`, {}, { headers: getAffHeaders() });
      toast.success("Telegram unlinked");
      onUpdate && onUpdate();
    } catch {
      toast.error("Unlink failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="profile-page">
      {/* Account Details */}
      <div className="rounded-xl border border-zinc-800/50 bg-zinc-900/80 backdrop-blur-md p-6" data-testid="account-details-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-blue-600/10 flex items-center justify-center">
            <User size={20} weight="duotone" className="text-blue-500" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-zinc-50" style={{ fontFamily: "Manrope" }}>Account Details</h3>
            <p className="text-[10px] text-zinc-500">Your profile information</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800">
            <div className="flex items-center gap-2 mb-1">
              <User size={12} className="text-zinc-500" />
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Name</p>
            </div>
            <p className="text-sm text-zinc-100 font-medium" data-testid="profile-name">{data.name || "-"}</p>
          </div>

          <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800">
            <div className="flex items-center gap-2 mb-1">
              <LinkIcon size={12} className="text-zinc-500" />
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Referral ID</p>
            </div>
            <p className="text-sm text-blue-400 font-mono font-bold" data-testid="profile-referral">{data.referral_code || "-"}</p>
          </div>

          <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800">
            <div className="flex items-center gap-2 mb-1">
              <Envelope size={12} className="text-zinc-500" />
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Email ID</p>
            </div>
            <p className="text-sm text-zinc-100" data-testid="profile-email">{data.email || "-"}</p>
          </div>

          <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800">
            <div className="flex items-center gap-2 mb-1">
              <Phone size={12} className="text-zinc-500" />
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Phone Number</p>
            </div>
            <p className="text-sm text-zinc-100" data-testid="profile-phone">{data.phone || "-"}</p>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="rounded-xl border border-zinc-800/50 bg-zinc-900/80 backdrop-blur-md p-6" data-testid="change-password-card">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
            <Key size={20} weight="duotone" className="text-amber-500" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-zinc-50" style={{ fontFamily: "Manrope" }}>Change Password</h3>
            <p className="text-[10px] text-zinc-500">Update your account password</p>
          </div>
        </div>

        <form onSubmit={handleChangePassword} className="space-y-3 max-w-md">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1 block">Current Password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input data-testid="current-password-input" type="password" placeholder="Enter current password"
                value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required
                className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1 block">New Password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input data-testid="new-password-input" type="password" placeholder="Enter new password (min 6 chars)"
                value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={6}
                className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1 block">Confirm Password</label>
            <div className="relative">
              <Check size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input data-testid="confirm-password-input" type="password" placeholder="Confirm new password"
                value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required
                className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100 text-sm" />
            </div>
          </div>
          <button type="submit" disabled={changing} data-testid="change-password-btn"
            className="px-6 py-2.5 rounded-lg text-xs font-bold bg-amber-600 text-white hover:bg-amber-500 transition-all disabled:opacity-50">
            {changing ? "Changing..." : "Update Password"}
          </button>
        </form>
      </div>

      {/* Telegram Notifications */}
      <div className="rounded-xl border border-zinc-800/50 bg-zinc-900/80 backdrop-blur-md p-6" data-testid="telegram-link-card">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-sky-500/10 flex items-center justify-center">
            <TelegramLogo size={20} weight="duotone" className="text-sky-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-zinc-50" style={{ fontFamily: "Manrope" }}>Telegram Notifications</h3>
            <p className="text-[10px] text-zinc-500">Get instant DMs when your MT5 account is approved or rejected</p>
          </div>
          {linkedChatId && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
              <Check size={10} weight="bold" />
              Linked
            </span>
          )}
        </div>

        {linkedChatId ? (
          <div className="p-4 rounded-lg bg-zinc-950 border border-zinc-800 flex items-center justify-between" data-testid="telegram-linked-state">
            <div>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Linked Chat ID</p>
              <p className="text-sm text-zinc-100 font-mono" data-testid="telegram-chat-id">{linkedChatId}</p>
            </div>
            <button
              onClick={handleUnlinkTelegram}
              data-testid="telegram-unlink-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-rose-500/10 text-rose-400 border border-rose-500/30 hover:bg-rose-500/20 transition-all"
            >
              <Trash size={12} />
              Unlink
            </button>
          </div>
        ) : (
          <form onSubmit={handleLinkTelegram} className="space-y-3 max-w-md" data-testid="telegram-link-form">
            <div className="p-3 rounded-lg bg-zinc-950/50 border border-zinc-800/50 text-[11px] text-zinc-400 leading-relaxed">
              <p className="mb-1.5 font-semibold text-zinc-300">How to get your Chat ID:</p>
              <ol className="list-decimal list-inside space-y-0.5">
                <li>Open Telegram and search for <span className="text-sky-400 font-mono">@userinfobot</span></li>
                <li>Start a chat and it will reply with your numeric Chat ID</li>
                <li>Paste that ID below</li>
              </ol>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1 block">Telegram Chat ID</label>
              <div className="relative">
                <TelegramLogo size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  data-testid="telegram-chat-id-input"
                  type="text"
                  inputMode="numeric"
                  placeholder="e.g. 123456789"
                  value={chatId}
                  onChange={(e) => setChatId(e.target.value)}
                  required
                  className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100 text-sm font-mono"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={linking}
              data-testid="telegram-link-btn"
              className="px-6 py-2.5 rounded-lg text-xs font-bold bg-sky-600 text-white hover:bg-sky-500 transition-all disabled:opacity-50"
            >
              {linking ? "Linking..." : "Link Telegram"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
